import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { HelpCircle } from "lucide-react";
import { useConsultation } from "../context/ConsultationContext";

const FAQ = [
  {
    q: "Mainland or Freezone — which is right for me?",
    a: "If you plan to trade inside the UAE, serve government clients or open retail branches, Mainland is the right choice. If you operate internationally, want 0% corporate tax on qualifying income, and prefer faster incorporation with flexible office options, a Freezone structure is usually better. StartINC advisors will map your activity, ownership, and 5-year plan before recommending a jurisdiction.",
  },
  {
    q: "How long does UAE company incorporation take?",
    a: "Freezone setups are typically completed in 7–14 working days. Mainland LLCs take 2–4 weeks depending on activity approvals and ejari. Visa stamping and bank account opening add another 2–4 weeks. End-to-end, most of our clients are fully operational within 4–6 weeks.",
  },
  {
    q: "What documents will I need to provide?",
    a: "At minimum: a valid passport copy for each shareholder, a recent passport-size photo, a CV or business profile, a proof of address (utility bill or bank statement) and your proposed business activities. For corporate shareholders we also need certificate of incorporation, MoA/AoA, and board resolution. Our team attests and legalises documents on your behalf.",
  },
  {
    q: "Can I own 100% of my UAE company as a foreigner?",
    a: "Yes. Post the 2021 reforms, 100% foreign ownership is available for the majority of Mainland activities, and Freezones have always allowed full foreign ownership. Certain strategic sectors (e.g. oil & gas, select professional categories) still require a UAE partner — StartINC will flag this upfront if it applies to your activity.",
  },
  {
    q: "How does the 9% UAE corporate tax affect my business?",
    a: "UAE corporate tax of 9% applies on taxable profits above AED 375,000. Qualifying Freezone Persons can continue to enjoy a 0% rate on qualifying income. StartINC structures your operations, transfer pricing, and substance so that you remain in the Qualifying Freezone Person regime wherever applicable.",
  },
  {
    q: "Will StartINC help me open a corporate bank account?",
    a: "Yes — and this is one of our differentiators. We pre-audit your corporate structure, UBO file and source-of-funds story, then introduce you directly to senior relationship managers at Emirates NBD, Wio, Mashreq, FAB, RAKBANK and select international banks. Our account approval rate is 98%.",
  },
  {
    q: "What about visas for me and my family?",
    a: "Depending on your share capital and activity, you can obtain a 2-year Investor Visa or a 10-year Golden Visa. Once your visa is issued you can sponsor your spouse, children, and parents. StartINC manages the entire immigration cycle — medical, Emirates ID, status change — in-house.",
  },
  {
    q: "Are your fees transparent? Any hidden costs?",
    a: "Every StartINC engagement starts with a written, itemised quotation covering license, government fees, immigration, documentation and service charges. There are no admin surcharges, no mandatory add-ons and no renewal surprises. If a fee changes (e.g. government authority revision), we flag it before charging.",
  },
];

export default function FAQSection() {
  const { open } = useConsultation();

  return (
    <section
      data-testid="faq-section"
      className="relative bg-[#F8FAFC] py-20 md:py-32"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="sticky top-28">
            <HelpCircle className="w-8 h-8 text-[#3366FF]" strokeWidth={1.5} />
            <p className="overline text-[#3366FF] mt-5">Frequently asked</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-3xl sm:text-4xl leading-tight">
              Everything founders ask <span className="font-black text-[#002366]">before</span> they sign.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Didn't find your answer? Book a free 20-minute call with a senior StartINC
              advisor — we'll give you a straight response in writing.
            </p>
            <button
              data-testid="faq-cta"
              onClick={() => open("other")}
              className="btn-navy mt-6"
            >
              Ask a senior advisor
            </button>
          </div>
        </div>

        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="rounded-sm border border-slate-200 bg-white px-5 md:px-7 data-[state=open]:border-[#3366FF]/40 data-[state=open]:shadow-md"
              >
                <AccordionTrigger className="font-heading font-semibold text-[#0F172A] text-left text-base md:text-lg tracking-tight py-5 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed text-sm md:text-base pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
